// @flow
import { View } from "extensions/marionette";
import { captureException } from "extensions/sentry";
import { Backbone } from "extensions/backbone";
import $ from "jquery";
import { _ } from "extensions/underscore";
import Radio from "backbone.radio";

import strings from "common/strings";

import filterableHeaderTemplate from "./headerTemplates/filterable.jst";
import sortableHeaderTemplate from "./headerTemplates/sortable.jst";
import regularHeaderTemplate from "./headerTemplates/regular.jst";
import queryableHeaderTemplate from "./headerTemplates/queryable.jst";
import queryableListElementTemplate from "./headerTemplates/queryableListElement.jst";

export type ColumnDataTypes = "text" | "amount" | "checkbox" | "action" | "number" | "rate" | "link";
export type ColumnType = {
    header: {
        title: string,
        tooltip: string
    },
    sortOptions: {
        active: boolean
    },
    cellOptions: {
        template: ?string,
        html: boolean,
        precision: ?number,
        types: { [key: string]: ColumnDataTypes },
        negativeFormat: string,
        currency: {
            attribute: string
        }
    },
    exportOptions: {},
    cellClassName?: string,
    cell: ColumnDataTypes
};

export const Model = Backbone.Model.extend({
    defaults: () => ({
        sortOptions: {
            active: false
        },
        cellOptions: {
            negativeFormat: "(%v)",
            currency: {
                attribute: "currency"
            },
            precision: void 0,
            html: false,
            template: void 0,
            types: {},
            emptyString: strings.emptyTableCell
        },
        exportOptions: {},
        header: {},
        ordering: "alphabetical"
    }),
    
    initialize(modelValues) {
        this.set({
            cellOptions: _.defaults({}, _.result(modelValues, "cellOptions", {}), this.defaults().cellOptions),
            sortOptions: _.defaults({}, modelValues.sortOptions, this.defaults().sortOptions)
        });
    }
});

export const HeaderView = View.extend({
    initialize() {},
    tagName: "th",
    events: {
        click: "onHeaderClick",
        "click .sortable": "applySort",
        "show.bs.dropdown": function(e) {
            this.dropdown = $(e.currentTarget).find(".dropdown-menu").detach();
            this.dropdown.css({
                display: "block",
                left: () => {
                    const closestHeader = this.$el.closest(".smarttable-header-static");
                    const closestHeaderScrollOffset = closestHeader.length ? closestHeader[0].scrollLeft : 0;
                    
                    return this.el.offsetLeft - closestHeaderScrollOffset;
                }
            });
            this.dropdown.on("click", "a.filterOption", this.applyFilter.bind(this));
            this.dropdown.on("click", ".sortable-ascending", this.applySortAscending.bind(this));
            this.dropdown.on("click", ".sortable-descending", this.applySortDescending.bind(this));
            this.dropdown.appendTo(this.$el.closest(".smarttable-header"));
        },
        "hide.bs.dropdown": function(e) {
            if (!this.dropdown) {
                return;
            }
            this.dropdown = this.dropdown.detach();
            this.dropdown.css("display", "none");
            this.dropdown.appendTo(this.$el);
            this.dropdown.off();
            this.dropdown = null;
        }
    },
    modelEvents: {
        "change:sortOptions": "render",
        "change:filterOptions": "render"
    },
    
    /**
     * Event handler to apply ascending sort by delegating to applySortGeneric.
     */
    applySortAscending(event) {
        this.applySortGeneric(event, "1");
    },
    
    /**
     * Event handler to apply descending sort by delegating to applySortGeneric.
     */
    applySortDescending(event) {
        this.applySortGeneric(event, "-1");
    },
    
    /**
     * Builds sort event call by getting sort options. Then raises sort event.
     */
    applySortGeneric(event, direction) {
        event.preventDefault();
        const cell = this.model.get("attribute");
        const cellType = this.model.get("cell");
    
        // Remove displaced dropdown
        this.dropdown.off().remove();
        this.dropdown = null;
        
        this.trigger("sort", this, cell, direction, cellType);
    },
    
    applySort(event) {
        event.preventDefault();
        // Get current value
        const cell = this.model.get("attribute");
        const cellType = this.model.get("cell");
        const sortOptions = this.model.get("sortOptions");
        let newSortDirection;
        
        if (sortOptions && sortOptions.active === "1") {
            newSortDirection = "-1";
        } else {
            newSortDirection = "1";
        }
        
        // Destroy a tooltip
        $(event.target).tooltip("destroy");
        
        // Inform header bros and table collection about the change
        this.trigger("sort", this, cell, newSortDirection, cellType);
    },
    
    onHeaderClick(event) {
        this.triggerMethod("click", this, this.model.get("attribute"), event);
    },
    
    applyFilter(event) {
        event.preventDefault();
        const cell = this.model.get("filterAttribute") || this.model.get("attribute");
        const $target = $(event.currentTarget);
        const filterBy = $target.addClass("active").attr("data-filter");
        
        // Modify value stored in the model
        const filterOptions = this.model.get("filterOptions");
        
        const newFilterOptions = _.map(filterOptions, (filterItem) => {
            if (filterItem.value === filterBy) {
                filterItem.active = true;
            } else {
                delete filterItem.active;
            }
            
            return filterItem;
        });
        
        this.model.set("filterOptions", newFilterOptions);
        
        // Really really force the filterOptions change
        this.render();
        
        // Remove displaced dropdown
        this.dropdown.off().remove();
        this.dropdown = null;
        
        // This event picked up in smartTable (childview:filter)
        this.trigger("filter", this, cell, filterBy);
    },
    
    /**
     * Sets class name for header to allow for css styling.
     */
    setFilteredItemHeaderClassName() {
        if (this.model.get("filterable") || this.model.get("queryable")) {
            const activeItem = this.model.get("filterOptions").find(({ active }) => active === true);
            if (activeItem) {
                this.$el.attr("class", `filter-${activeItem.value.toString()} ${activeItem.headerClassName || ""}`);
            }
        }
    },
    
    attributes() {
        let base = {
            scope: "col"
        };
        const headerOptions = this.model.get("header");
        
        if (headerOptions) {
            base = Object.assign(base, headerOptions.attributes);
        }
        
        return base;
    },
    
    template(column) {
        let template;
        
        if (column.filterable) {
            template = filterableHeaderTemplate;
        } else if (column.sortable && !column.queryable) {
            template = sortableHeaderTemplate;
        } else if (column.queryable) {
            // Aditional logic for async template loading
            template = queryableHeaderTemplate;
        } else {
            template = regularHeaderTemplate;
        }
        return template(column);
    },
    
    serializeData() {
        let data = {};
        if (this.model) {
            data = this.serializeModel(this.model);
        }
        
        if (_.isFunction(data.name)) {
            data.name = data.name(data);
        }
        
        return data;
    },
    
    templateContext() {
        return {
            cid: this.cid
        };
    },
    
    /**
     * Adds list of filter options to template
     */
    filterOptionsApplyList() {
        const self = this;
        const filterOptions = _.clone(self.model.get("filterOptions"));
        
        self.$el.find("div.async-template").html("");
        
        // Keep first item of the array and sort the rest alphabetically
        [filterOptions.shift()].concat(filterOptions.sort((a, b) => {
            const valA = a.name.toLowerCase();
            const valB = b.name.toLowerCase();
            
            if (valA > valB) {
                return 1;
            }
            
            if (valA < valB) {
                return -1;
            }
            return 0;
        })).forEach((data) => {
            const template = queryableListElementTemplate;
            const compiled = template(data);
            self.$el.find("div.async-template").append(compiled);
        });
    },
    
    /**
     * Applies header styles according to active filter items
     */
    filterOptionsApplyHeaderStyles() {
        const filterOptions = this.model.get("filterOptions");
        const activeItem = _.findWhere(filterOptions, { active: true });
        
        if (activeItem) {
            this.setFilteredItemHeaderClassName();
        }
    },
    
    /**
     * Attaches filterOptions to self using this.activeRender
     */
    downloadFilterOptions() {
        const afterRender = this.model.get("afterRender");
        
        afterRender(Radio.channel("main").request("get:merchant"), (error, listData) => {
            if (error) {
                captureException(error);
                return;
            }
            
            this.model.set("filterOptions", listData);
        });
    },
    /**
     * Rendering function for table type of queryable
     */
    queryableFilterOptionsRender() {
        if (this.model.has("filterOptions")) {
            this.filterOptionsApplyList();
            this.filterOptionsApplyHeaderStyles();
        } else {
            this.downloadFilterOptions();
        }
    },
    
    isActivelySorted() {
        const hasSortOptions = this.model.has("sortOptions");
        return hasSortOptions && ["1", "-1"].includes(this.model.get("sortOptions").active);
    },
    
    onRender() {
        if (this.model.get("queryable") && this.model.has("afterRender")) {
            this.queryableFilterOptionsRender();
        }
        
        if (this.model.get("filterable")) {
            this.setFilteredItemHeaderClassName();
        }
        
        if (this.model.has("cellClassName")) {
            this.$el.addClass(this.model.get("cellClassName"));
        }
        
        // Add className of the cell type
        const cellType = this.model.get("cell");
        this.$el.addClass(cellType);
        
        // If the cell is a `text` type and has a template with types, add type classes
        if (cellType === "text" && this.model.get("cellOptions").template && this.model.get("cellOptions").types) {
            this.$el.addClass(Object.keys(this.model.get("cellOptions").types).map((k) => `text-template-${k}`).join(" "));
        }
        
        // Add feature-specific class name
        ["sortable", "filterable", "queryable"].forEach((feature) => {
            this.$el.toggleClass(`feature-${feature}`, !!this.model.get(feature));
        });
        
        // Mark as "sorted" if the column is currently sorted by this model's value
        this.$el.toggleClass("sorted", this.isActivelySorted());
    },
    
    onDestroy() {
        if (this.dropdown) {
            this.dropdown.remove();
        }
    }
});

export { Presenter } from "./Presenter";
